import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Megaphone, Sparkles, Copy, Loader2, Download, Youtube, Instagram, Music2,
  Image as ImageIcon, Video, Type, MonitorPlay,
} from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = [
  { value: "youtube", label: "YouTube", icon: Youtube },
  { value: "instagram", label: "Instagram", icon: Instagram },
  { value: "tiktok", label: "TikTok", icon: Music2 },
];
const OBJECTIVES = [
  { value: "get_leads", label: "Get leads" },
  { value: "drive_traffic", label: "Drive traffic" },
  { value: "make_sales", label: "Make sales" },
];

export default function AdCreator() {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [objective, setObjective] = useState("get_leads");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [listLoading, setListLoading] = useState(true);

  useEffect(() => {
    api.get("/campaigns", { params: { all: true } }).then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setCampaigns(list);
      if (list.length) setCampaignId(list[0].id);
    }).catch((e) => {
      console.error("Could not load campaigns:", e);
      toast.error("Couldn't load your campaigns");
    }).finally(() => setListLoading(false));
  }, []);

  const generate = async () => {
    if (!campaignId) { toast.error("Pick a campaign first"); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/ad-creator/generate", {
        campaign_id: campaignId, platform, objective,
      });
      setResult(data.ad_creator);
      toast.success("Your ads are ready!");
    } catch (e) {
      if (e.response?.status !== 402)
        toast.error(e.response?.data?.detail || "Generation failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    toast.success("Copied!");
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/ad-creator/${campaignId}/export-pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ad-creator.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not download the PDF. Please try again.");
    }
  };

  const PlatformIcon = PLATFORMS.find((p) => p.value === (result?.platform || platform))?.icon || Megaphone;

  return (
    <div className="space-y-8" data-testid="ad-creator-page">
      <div className="fade-up flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Megaphone size={24} />
        </div>
        <div>
          <h1 className="font-head text-4xl font-bold">Ad Creator</h1>
          <p className="text-muted-foreground">
            Turn any campaign into platform-ready ads — copy plus a creative concept for each.
          </p>
        </div>
      </div>

      <Card className="fade-up border-border p-6" data-testid="ad-creator-controls">
        <div className="grid gap-5 sm:grid-cols-3">
          <div className="space-y-2">
            <Label>Campaign</Label>
            <Select value={campaignId} onValueChange={setCampaignId} disabled={listLoading || !campaigns.length}>
              <SelectTrigger data-testid="ad-campaign-select">
                <SelectValue placeholder={listLoading ? "Loading…" : "Select a campaign"} />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name || c.topic || "Untitled campaign"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Platform</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger data-testid="ad-platform-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Objective</Label>
            <Select value={objective} onValueChange={setObjective}>
              <SelectTrigger data-testid="ad-objective-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OBJECTIVES.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!listLoading && !campaigns.length && (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="ad-no-campaigns">
            You don't have any campaigns yet. Create one first, then come back to build its ads.
          </p>
        )}

        <Button onClick={generate} disabled={loading || !campaignId} data-testid="ad-generate-btn"
          className="mt-6 rounded-full bg-primary px-7 py-6 text-base font-semibold hover:bg-primary/90">
          {loading
            ? <><Loader2 size={18} className="mr-1.5 animate-spin" /> Writing your ads…</>
            : <><Sparkles size={18} className="mr-1.5" /> Generate ads</>}
        </Button>
      </Card>

      {result && (
        <div className="space-y-4" data-testid="ad-creator-results">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <PlatformIcon size={16} className="text-primary" />
              {PLATFORMS.find((p) => p.value === result.platform)?.label} ·{" "}
              {OBJECTIVES.find((o) => o.value === result.objective)?.label}
            </div>
            <Button onClick={exportPdf} variant="outline" size="sm" data-testid="ad-export-pdf-btn"
              className="gap-1.5 rounded-full">
              <Download size={15} /> Download PDF
            </Button>
          </div>

          {result.variations.map((v, i) => (
            <Card key={i} className="fade-up border-border p-6" data-testid={`ad-variation-${i}`}>
              <div className="mb-3 flex items-center justify-between">
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
                  Variation {i + 1}
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => copy(v.copy)} data-testid={`ad-copy-btn-${i}`} title="Copy ad copy"
                    className="rounded-full border border-border p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-primary">
                    <Copy size={15} />
                  </button>
                </div>
              </div>

              <p className="whitespace-pre-line text-sm leading-relaxed">{v.copy}</p>

              <div className="mt-4 rounded-xl border border-border bg-secondary/40 p-4">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-primary">
                  {v.concept?.format?.toLowerCase().includes("video")
                    ? <Video size={14} /> : <ImageIcon size={14} />}
                  Creative concept{v.concept?.format ? ` · ${v.concept.format}` : ""}
                </p>
                {v.concept?.visual && (
                  <p className="text-sm leading-relaxed">{v.concept.visual}</p>
                )}
                {v.concept?.on_screen && (
                  <p className="mt-2 flex items-start gap-1.5 text-sm text-muted-foreground">
                    <MonitorPlay size={14} className="mt-0.5 shrink-0" />
                    <span><span className="font-semibold text-foreground">On screen:</span> {v.concept.on_screen}</span>
                  </p>
                )}
                {v.concept?.overlay && (
                  <p className="mt-1.5 flex items-start gap-1.5 text-sm text-muted-foreground">
                    <Type size={14} className="mt-0.5 shrink-0" />
                    <span><span className="font-semibold text-foreground">Text overlay:</span> {v.concept.overlay}</span>
                  </p>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
